import { Box, Grid, Theme, useTheme } from '@mui/material';
import syntiro_logo_horizontal_white_text_slogan from '../../assets/syntiro-logo-horizontal-white-text-slogan.svg';
import gri_logo from '../../assets/gri-logo-white.webp';
import un_global_compact_logo from '../../assets/un-global-compact-logo.webp';
import ghg_protocol_logo from '../../assets/ghg-protocol-logo.webp';
import gbcsa_logo from '../../assets/gbcsa-logo.webp';
import { CaptionText, MinorHeading, OverlineText } from '../shared/typography/PrimaryTypography';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PrimaryDivider from '../shared/decoration/PrimaryDivider';

interface FooterWebsiteProps {
  handleDisplayLoginModal: (display: boolean) => void;
}

/**
 * Footer of logged out web views displaying additional links and information
 * @param {void} handleDisplayLoginModal Set the display state of the login client modal.
 * @returns {JSX.Element}
 */
const FooterWebsite = ({ handleDisplayLoginModal }: FooterWebsiteProps) => {
  const theme: Theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        paddingTop: 12,
        paddingLeft: { xs: 2, md: 12 },
        paddingRight: { xs: 2, md: 12 },
        paddingBottom: 4
      }}
    >
      <Grid
        container
        spacing={{ xs: 4, md: 6, lg: 12 }}
        sx={{ alignItems: 'center', mb: { xs: 4, lg: 12 } }}
      >
        <Grid
          size={{ xs: 12, md: 6, lg: 4 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'start'
          }}
        >
          <img
            src={syntiro_logo_horizontal_white_text_slogan}
            alt="Syntiro"
            style={{ width: '90%', maxWidth: 300 }}
          />
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={'auto'}>
              <a href="https://www.globalreporting.org/" target="_blank/">
                <img src={gri_logo} alt="GRI" height={30} />
              </a>
            </Grid>
            <Grid size={'auto'}>
              <a href="https://unglobalcompact.org/" target="_blank/">
                <img src={un_global_compact_logo} alt="United Nations Global Compact" height={30} />
              </a>
            </Grid>
            <Grid size={'auto'}>
              <a href="https://www.gbcsa.org.za/" target="_blank/">
                <img src={gbcsa_logo} alt="GBCSA" height={30} />
              </a>
            </Grid>
            <Grid size={'auto'}>
              <a href="https://ghgprotocol.org/" target="_blank/">
                <img src={ghg_protocol_logo} alt="GHG Protocol" height={30} />
              </a>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ minHeight: 175, textAlign: 'start' }}>
          <MinorHeading weight="Bold" dark sx={{ mb: 2 }}>
            Contact Us
          </MinorHeading>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <EmailIcon fontSize="small" color="secondary" sx={{ mr: 1 }} />
            <a href="mailto:sustainability@energypartners.co.za" style={{ textDecoration: 'none' }}>
              <CaptionText weight="Medium" dark>
                sustainability@energypartners.co.za
              </CaptionText>
            </a>
          </Box>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <LocalPhoneIcon fontSize="small" color="secondary" sx={{ mr: 1 }} />
            <CaptionText weight="Medium" dark>
              +27 (0)21 941 5140
            </CaptionText>
          </Box>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <LocationOnIcon fontSize="small" color="secondary" sx={{ mr: 1 }} />
            <CaptionText weight="Medium" dark>
              Unit 2, White Oak Terraces, Old Oak Office Park, 2 Edmar Street, Cape Town, South
              Africa
            </CaptionText>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <CaptionText weight="Medium" dark sx={{ textAlign: 'left' }}>
              Energy Partners Holdings (Pty) Ltd is a B-BBEE Level 1-accredited provider.
            </CaptionText>
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6, lg: 2 }}
          sx={{ minHeight: { xs: 0, md: 175 }, textAlign: 'start' }}
        >
          <MinorHeading weight="Bold" dark sx={{ mb: 2 }}>
            Quick Links
          </MinorHeading>
          <CaptionText
            dark
            weight="Medium"
            sx={{ mb: 1, cursor: 'pointer', display: { xs: 'none', lg: 'block' } }}
            onClick={() => handleDisplayLoginModal(true)}
          >
            Client Login
          </CaptionText>
          <a href="/contact" style={{ textDecoration: 'none' }}>
            <CaptionText weight="Medium" dark sx={{ mb: 1 }}>
              Request a Demo
            </CaptionText>
          </a>
          <a href={'/#solutions'} style={{ textDecoration: 'none' }}>
            <CaptionText weight="Medium" dark sx={{ mb: 1 }}>
              Solutions
            </CaptionText>
          </a>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6, lg: 2 }}
          sx={{ minHeight: { xs: 0, md: 175 }, textAlign: 'start' }}
        >
          <MinorHeading weight="Bold" dark sx={{ mb: 2 }}>
            Follow Us
          </MinorHeading>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <a href="https://www.linkedin.com/company/energy-partners-" target="_blank/">
              <LinkedInIcon color="secondary" fontSize="large" />
            </a>
            <a href="https://www.youtube.com/channel/UCH-UiZevfEtbN4nuvegfIFA" target="_blank/">
              <YouTubeIcon color="secondary" fontSize="large" />
            </a>
          </Box>
        </Grid>
      </Grid>
      <PrimaryDivider sx={{ my: 2, opacity: 0.3 }} />
      <Box sx={{ display: 'flex' }}>
        <OverlineText weight="Medium" dark>
          Syntiro © 2020 - 2026. All Rights Reserved.
        </OverlineText>
      </Box>
    </Box>
  );
};

export default FooterWebsite;
